(() => {
  'use strict';
  const drawer = document.querySelector('[data-eb-cart-drawer]');
  if (!drawer || typeof drawer.showModal !== 'function') return;
  let busy = false;
  let returnFocus = null;
  let opening = null;
  let revision = 0;
  const contents = () => document.querySelector('[data-eb-cart-content]');
  const pageBar = document.querySelector('[data-eb-cart-page-bar]');
  let pageCheckoutObserver;
  const observePageCheckout = () => {
    if (!pageBar) return;
    pageCheckoutObserver?.disconnect();
    const page = document.querySelector('[data-eb-cart-page]');
    const checkout = page.querySelector('.eb-cart-totals [name="checkout"]');
    if (!checkout) { pageBar.hidden = true; return; }
    pageBar.querySelector('[data-eb-cart-bar-total]').textContent = page.querySelector('.eb-cart-total dd').textContent;
    pageBar.hidden = false;
    if ('IntersectionObserver' in window) {
      pageCheckoutObserver = new IntersectionObserver(entries => { pageBar.hidden = entries[0].isIntersecting; }, {threshold: .1});
      pageCheckoutObserver.observe(checkout);
    }
  };
  const announce = (message, error = false) => {
    document.querySelectorAll('[data-eb-cart-status]').forEach(node => {
      node.textContent = message;
      node.classList.toggle('is-error', error);
    });
  };
  const setBusy = value => {
    busy = value;
    if (pageBar) pageBar.querySelector('button').disabled = value;
    document.querySelectorAll('[data-eb-cart-form]').forEach(form => {
      form.setAttribute('aria-busy', String(value));
      form.querySelectorAll('button, input').forEach(control => {
        if (value && !control.hasAttribute('data-eb-busy-disabled')) {
          control.dataset.ebBusyDisabled = String(control.disabled);
          control.disabled = true;
        } else if (!value && control.hasAttribute('data-eb-busy-disabled')) {
          control.disabled = control.dataset.ebBusyDisabled === 'true';
          delete control.dataset.ebBusyDisabled;
        }
      });
    });
  };
  const render = html => {
    if (!html) throw new Error(contents().dataset.errorMessage);
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-eb-cart-content]');
    if (!source) throw new Error(contents().dataset.errorMessage);
    document.querySelectorAll('[data-eb-cart-content]').forEach(node => {
      const replacement = source.cloneNode(true);
      const form = replacement.querySelector('[data-eb-cart-form]');
      if (form) form.id = node.closest('[data-eb-cart-page]') ? 'EXYRCartFormPage' : 'EXYRCartFormDrawer';
      node.replaceWith(replacement);
    });
    document.querySelectorAll('[data-eb-cart-count]').forEach(node => { node.textContent = source.dataset.itemCount; });
    observePageCheckout();
  };
  const load = async () => {
    const currentRevision = ++revision;
    const cart = contents();
    const url = new URL(cart.dataset.cartUrl, window.location.origin);
    url.searchParams.set('section_id', cart.dataset.section);
    const response = await fetch(url.toString(), {credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000)});
    if (!response.ok) throw new Error(cart.dataset.errorMessage);
    const html = await response.text();
    if (currentRevision === revision) render(html);
  };
  const open = async (opener, html) => {
    returnFocus = opener || document.activeElement;
    if (!drawer.open) drawer.showModal();
    document.documentElement.classList.add('eb-cart-is-open');
    document.querySelectorAll('.eb-bag').forEach(link => link.setAttribute('aria-expanded', 'true'));
    announce(contents().dataset.loadingMessage);
    try {
      if (html) { revision += 1; render(html); }
      else await load();
      announce('');
    } catch (error) {
      drawer.close();
      window.location.assign(contents().dataset.cartUrl);
    }
  };
  const change = async (key, quantity, variant, focusAction) => {
    if (busy) return;
    revision += 1;
    const cart = contents();
    const message = cart.dataset.updateMessage;
    const errorMessage = cart.dataset.errorMessage;
    announce(cart.dataset.loadingMessage);
    setBusy(true);
    try {
      const response = await fetch(cart.dataset.changeUrl + '.js', {
        method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(15000),
        headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
        body: JSON.stringify({id: key, quantity, sections: [cart.dataset.section], sections_url: window.location.pathname})
      });
      const result = await response.json();
      if (!response.ok) {
        const problem = new Error(result.description || result.message || errorMessage);
        problem.cartMessage = true;
        throw problem;
      }
      if (result.sections?.[cart.dataset.section]) render(result.sections[cart.dataset.section]);
      else await load();
      announce(message);
      if (variant && focusAction) {
        const surface = drawer.open ? drawer : document.querySelector('[data-eb-cart-page]');
        const line = surface?.querySelector(`[data-variant-id="${CSS.escape(variant)}"]`);
        const control = line?.querySelector(focusAction);
        if (control && !control.disabled) control.focus({preventScroll: true});
        else surface?.querySelector('[data-eb-cart-quantity], [name="checkout"], a')?.focus({preventScroll: true});
      } else {
        const surface = drawer.open ? drawer : document.querySelector('[data-eb-cart-page]');
        surface?.querySelector('[data-eb-cart-quantity], [name="checkout"], [data-eb-cart-content] a')?.focus({preventScroll: true});
      }
    } catch (error) {
      try { await load(); } catch (refreshError) { window.location.assign(cart.dataset.cartUrl); }
      announce(error.cartMessage ? error.message : errorMessage, true);
    } finally {
      setBusy(false);
    }
  };
  document.querySelectorAll('.eb-bag').forEach(link => {
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', drawer.id);
    link.setAttribute('aria-expanded', 'false');
  });
  observePageCheckout();
  document.addEventListener('click', event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const close = event.target.closest('[data-eb-cart-close]');
    if (close && drawer.open) {
      drawer.close();
      if (close.tagName === 'BUTTON') event.preventDefault();
      return;
    }
    const bag = event.target.closest('.eb-bag');
    if (bag) {
      event.preventDefault();
      if (!opening) opening = open(bag).finally(() => { opening = null; });
      return;
    }
    const action = event.target.closest('[data-eb-cart-step], [data-eb-cart-remove], [data-eb-cart-upgrade]');
    if (!action) return;
    event.preventDefault();
    if (busy || action.disabled) return;
    const line = action.closest('[data-eb-cart-line]');
    const input = line?.querySelector('[data-eb-cart-quantity]');
    if (action.hasAttribute('data-eb-cart-upgrade')) {
      change(action.dataset.lineKey, Number(action.dataset.ebCartUpgrade));
    } else if (action.hasAttribute('data-eb-cart-remove')) {
      change(line.dataset.lineKey, 0);
    } else {
      const step = Number(input.step) || 1;
      let quantity = Number(input.value) + Number(action.dataset.ebCartStep) * step;
      if (quantity < Number(input.min)) quantity = 0;
      if (input.max) quantity = Math.min(quantity, Number(input.max));
      change(line.dataset.lineKey, quantity, line.dataset.variantId, `[data-eb-cart-step="${action.dataset.ebCartStep}"]`);
    }
  });
  const changeInput = input => {
    if (busy) return;
    if (!input.checkValidity()) { input.reportValidity(); return; }
    const line = input.closest('[data-eb-cart-line]');
    change(line.dataset.lineKey, Number(input.value), line.dataset.variantId, '[data-eb-cart-quantity]');
  };
  document.addEventListener('change', event => { if (event.target.matches('[data-eb-cart-quantity]')) changeInput(event.target); });
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-eb-cart-quantity]')) {
      event.preventDefault();
      changeInput(event.target);
    }
  });
  document.addEventListener('submit', event => { if (busy && event.target.matches('[data-eb-cart-form]')) event.preventDefault(); });
  window.addEventListener('eb:cart-added', event => {
    event.preventDefault();
    open(event.detail.opener, event.detail.sections?.['eb-cart-render']);
  });
  window.addEventListener('eb:cart-refresh', () => { load().catch(() => {}); });
  drawer.addEventListener('close', () => {
    document.documentElement.classList.remove('eb-cart-is-open');
    document.querySelectorAll('.eb-bag').forEach(link => link.setAttribute('aria-expanded', 'false'));
    if (returnFocus?.isConnected) returnFocus.focus({preventScroll: true});
  });
  drawer.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const controls = [...drawer.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(control => control.getClientRects().length > 0 && !control.closest('[hidden]'));
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  drawer.addEventListener('click', event => {
    if (event.target !== drawer) return;
    const bounds = drawer.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) drawer.close();
  });
})();
